/* eslint-disable max-lines -- Pre-existing violation */
import {
  Box,
  Show,
  Space,
  Spinner,
  ThemeColorPath,
  ThemeIconKey,
  ThemeLottieKey,
  type ThemeShadowsPath,
} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match, Pattern} from 'ts-pattern';

import {FocusEventHandler, HTMLAttributeAnchorTarget, MouseEventHandler, useRef} from 'react';
import {Link, To} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {getSafely, suffixTestId, TestIdProps} from 'shared/web';

import {KeyboardShortcut} from '../../constants/KeyboardShortcut';
import {KEYBOARD_SHORTCUT_DEFINITIONS} from '../../constants/keyboardShortcutDefinitions';
import {getKeyboardShortcutLabel} from '../../utils/getKeyboardShortcutLabel';
import {Badge, BadgeColorScheme} from '../Badge/Badge';
import {Tooltip} from '../Tooltip/Tooltip';
import {IconOrLottie} from './IconOrLottie';
import {useKeyboardShortcut} from './useKeyboardShortcut';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'warning'
  | 'success'
  | 'outlined'
  | 'ghost'
  | 'ghostLink'
  | 'errorLink'
  | 'link'
  | 'dangerOutlined'
  | 'dangerGhost'
  | 'dangerLink';

export type ButtonSize = 'small' | 'default';

export type ButtonBoxShadow = Extract<ThemeShadowsPath, 'elevation_2'>;
export interface ButtonProps extends TestIdProps {
  title?: string;
  /** Shown after the title, typically in parentheses (e.g. vehicle count). Rendered with smaller text. */
  suffix?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
  isLoading?: boolean;
  isFullWidth?: boolean;
  isDisabled?: boolean;
  isFocused?: boolean;
  wrap?: boolean;
  leftIcon?: ThemeIconKey | ThemeLottieKey;
  rightIcon?: ThemeIconKey | ThemeLottieKey;
  badge?: {value: string; colorScheme: BadgeColorScheme};
  iconColor?: ThemeColorPath;
  type?: 'submit' | 'reset' | 'button';
  tabIndex?: number;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onBlur?: FocusEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  /**
   * Registers a global shortcut that invokes this button's click handler.
   */
  keyboardShortcut?: KeyboardShortcut;
  /**
   * Button element HTML5 attribute used in FormButton
   */
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  form?: string;
  to?: To;
  target?: HTMLAttributeAnchorTarget;
  replace?: boolean;
  boxShadow?: ButtonBoxShadow;
}

const buttonVariants = cva(
  'relative inline-flex cursor-pointer items-center justify-center whitespace-nowrap !text-[length:var(--text-size-small)] font-[var(--text-weight-default)] leading-[var(--text-leading-small)] tracking-[0] transition-all duration-[250ms] focus-visible:outline-none focus-visible:[outline-color:currentColor] focus-visible:[outline-width:3px] disabled:cursor-not-allowed disabled:opacity-40',
  {
    variants: {
      size: {
        default: 'h-8 px-3 py-[6px]',
        small: 'h-6 px-2 py-[2px]',
      },
      variant: {
        primary:
          'rounded-sm bg-[var(--color-accent)] text-[var(--color-white)] hover:bg-[var(--color-palettes-blue-70-100)] active:bg-[var(--color-palettes-blue-80-100)] focus-visible:bg-[var(--color-accent)] focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-[var(--color-accent)]',
        secondary:
          'rounded-sm bg-[var(--color-palettes-neutral-30-100)] text-[var(--color-primary)] hover:bg-[var(--color-palettes-neutral-40-100)] active:bg-[var(--color-palettes-neutral-50-100)] focus-visible:bg-[var(--color-palettes-neutral-30-100)] focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-[var(--color-palettes-neutral-30-100)]',
        outlined:
          'rounded-sm border border-[var(--color-palettes-neutral-70-100)] bg-[var(--color-white)] text-[var(--color-primary)] hover:border-[var(--color-palettes-neutral-200-100)] hover:bg-[var(--color-white)] active:border-[var(--color-palettes-neutral-900-100)] active:bg-[var(--color-white)] focus-visible:bg-[var(--color-white)] focus-visible:[box-shadow:0_0_0_1px_var(--color-palettes-neutral-70-100)] disabled:border-[var(--color-palettes-neutral-70-100)] disabled:bg-[var(--color-white)]',
        link: 'rounded-sm bg-transparent px-0 text-[var(--color-link)] hover:bg-transparent hover:underline active:bg-transparent focus-visible:bg-transparent focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-transparent',
        ghost:
          'rounded-sm bg-transparent hover:bg-[var(--color-palettes-neutral-30-100)] active:bg-[var(--color-palettes-neutral-40-100)] focus-visible:bg-transparent focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-transparent',
        ghostLink:
          'rounded-sm bg-transparent text-[var(--color-link)] hover:bg-[var(--color-palettes-neutral-30-100)] active:bg-[var(--color-palettes-neutral-40-100)] focus-visible:bg-transparent focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-transparent',
        errorLink:
          'rounded-sm bg-transparent text-[var(--color-danger)] hover:bg-transparent hover:underline active:bg-transparent focus-visible:bg-transparent focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-transparent',
        danger:
          'rounded-sm bg-[var(--color-severity-danger)] text-[var(--color-white)] hover:bg-[var(--color-palettes-red-70-100)] active:bg-[var(--color-palettes-red-80-100)] focus-visible:bg-[var(--color-severity-danger)] focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-[var(--color-severity-danger)]',
        warning:
          'rounded-sm bg-[var(--color-severity-warning)] text-[var(--color-white)] hover:bg-[var(--color-palettes-orange-70-100)] active:bg-[var(--color-palettes-orange-80-100)] focus-visible:bg-[var(--color-severity-warning)] focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-[var(--color-severity-warning)]',
        success:
          'rounded-sm bg-[var(--color-severity-success)] text-[var(--color-white)] hover:bg-[var(--color-palettes-green-70-100)] active:bg-[var(--color-palettes-green-80-100)] focus-visible:bg-[var(--color-severity-danger)] focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-[var(--color-severity-danger)]',
        dangerOutlined:
          'rounded-sm border border-[var(--color-severity-danger)] bg-[var(--color-white)] text-[var(--color-danger)] hover:border-[var(--color-palettes-red-80-100)] hover:bg-[var(--color-white)] active:border-[var(--color-palettes-red-90-100)] active:bg-[var(--color-white)] focus-visible:bg-[var(--color-white)] focus-visible:[box-shadow:0_0_0_1px_var(--color-severity-danger)] disabled:border-[var(--color-severity-danger)] disabled:bg-[var(--color-white)]',
        dangerLink:
          'rounded-sm bg-transparent p-0 text-[var(--color-danger)] hover:bg-transparent hover:underline active:bg-transparent focus-visible:bg-transparent focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-transparent',
        dangerGhost:
          'rounded-sm bg-transparent text-[var(--color-danger)] hover:bg-[var(--color-palettes-neutral-30-100)] active:bg-[var(--color-palettes-neutral-40-100)] focus-visible:bg-transparent focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)] disabled:bg-transparent',
      },
      isLoading: {
        true: 'pointer-events-none overflow-hidden',
        false: '',
      },
      isFullWidth: {
        true: 'min-w-full',
        false: '',
      },
      boxShadow: {
        true: '[box-shadow:var(--shadow-elevation-2)]',
        false: '',
      },
    },
    compoundVariants: [
      {
        size: 'small',
        variant: 'link',
        className:
          'h-4 !text-[length:var(--text-size-x-small)] leading-[var(--text-leading-x-small)]',
      },
    ],
    defaultVariants: {
      size: 'default',
      variant: 'primary',
      isLoading: false,
      isFullWidth: false,
      boxShadow: false,
    },
  }
);

const focusedButtonVariants = cva(
  '[outline:var(--outline-default)_var(--color-accent)] [outline-offset:var(--outline-offset)] focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-blue-40-100)]',
  {
    variants: {
      tone: {
        default: '',
        danger:
          '[outline-color:var(--color-severity-danger)] focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-red-40-100)]',
        warning:
          '[outline-color:var(--color-severity-warning)] focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-orange-40-100)]',
        success:
          '[outline-color:var(--color-severity-success)] focus-visible:[box-shadow:0_0_0_4px_var(--color-palettes-green-40-100)]',
      },
    },
    defaultVariants: {
      tone: 'default',
    },
  }
);

function getFocusTone(variant: ButtonVariant | undefined) {
  return match(variant)
    .with('danger', 'dangerOutlined', 'dangerGhost', 'dangerLink', () => 'danger' as const)
    .with('warning', () => 'warning' as const)
    .with('success', () => 'success' as const)
    .otherwise(() => 'default' as const);
}

function getButtonClassName(props: ButtonProps) {
  return cn(
    buttonVariants({
      size: props.size,
      variant: props.variant,
      isLoading: props.isLoading,
      isFullWidth: props.isFullWidth,
      boxShadow: isNotNil(props.boxShadow),
    }),
    props.isFocused ? focusedButtonVariants({tone: getFocusTone(props.variant)}) : undefined,
    props.wrap
      ? cn(
          'h-auto max-w-full whitespace-normal text-left [overflow-wrap:anywhere]',
          props.isFullWidth ? 'min-w-full' : 'min-w-0'
        )
      : undefined
  );
}

export function Button(props: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const linkRef = useRef<HTMLAnchorElement | null>(null);

  useKeyboardShortcut({
    shortcut: props.keyboardShortcut,
    buttonRef,
    linkRef,
    isDisabled: props.isDisabled,
    isLoading: props.isLoading,
  });

  const shortcutLabel = isNotNil(props.keyboardShortcut)
    ? getKeyboardShortcutLabel(props.keyboardShortcut)
    : undefined;
  const ariaKeyShortcuts = isNotNil(props.keyboardShortcut)
    ? getSafely(KEYBOARD_SHORTCUT_DEFINITIONS, props.keyboardShortcut)?.ariaKeyShortcuts
    : undefined;

  const content = (
    <>
      <Show when={props.isLoading}>
        <Spinner variant="overlay" size="small" data-testid={suffixTestId('button', props)} />
      </Show>

      <ButtonIcon value={props.leftIcon} color={props.iconColor} paddingRight />

      {props.title}

      <Show when={props.suffix}>
        <Space horizontal={1} />
        <span className="text-[length:var(--text-size-x-small)] leading-[var(--text-leading-x-small)] text-inherit">
          {props.suffix}
        </span>
      </Show>

      <Show when={props.badge}>
        <Space horizontal={2} />
        <Badge
          value={props.badge?.value ?? ''}
          colorScheme={props.badge?.colorScheme}
          size="small"
        />
      </Show>

      <ButtonIcon value={props.rightIcon} color={props.iconColor} paddingLeft />
    </>
  );

  const buttonElement = props.to ? (
    <Link
      ref={linkRef}
      aria-label={props['aria-label']}
      aria-keyshortcuts={ariaKeyShortcuts}
      to={props.to}
      target={props.target}
      replace={props.replace}
      className={getButtonClassName(props)}
      tabIndex={props.tabIndex}
      data-testid={suffixTestId('button', props)}
      onClick={props.onClick}
      onBlur={props.onBlur}
    >
      {content}
    </Link>
  ) : (
    <button
      ref={buttonRef}
      aria-label={props['aria-label']}
      aria-expanded={props['aria-expanded']}
      aria-controls={props['aria-controls']}
      aria-keyshortcuts={ariaKeyShortcuts}
      type={props.type ?? 'button'}
      disabled={props.isDisabled}
      className={getButtonClassName(props)}
      form={props.form}
      tabIndex={props.tabIndex}
      onClick={props.onClick}
      onBlur={props.onBlur}
      data-testid={suffixTestId('button', props)}
    >
      {content}
    </button>
  );

  if (isNil(shortcutLabel)) {
    return buttonElement;
  }

  return (
    <Tooltip label={shortcutLabel} shouldWrapperStretch={props.isFullWidth}>
      {buttonElement}
    </Tooltip>
  );
}

interface ButtonIconProps {
  value?: ThemeIconKey | ThemeLottieKey;
  color?: ThemeColorPath;
  paddingLeft?: boolean;
  paddingRight?: boolean;
}

function ButtonIcon(props: ButtonIconProps) {
  const icon = match(props.value)
    .with(Pattern.when(isNotNil), (value) => <IconOrLottie value={value} color={props.color} />)
    .otherwise(() => null);

  return (
    <Show when={props.value}>
      <Box
        paddingLeft={props.paddingLeft ? 1 : undefined}
        paddingRight={props.paddingRight ? 1 : undefined}
      >
        {icon}
      </Box>
    </Show>
  );
}
